/**
 * Timestamped bookmarks: the only way to find a passage again in twenty hours
 * of audio.
 *
 * A slide over panel on the opposite edge from ChapterNav, so the two never
 * fight for the same fifth of the screen if a caller ever needs both mounted.
 * Every mutation (add, rename, delete) updates the local list straight from
 * the server's answer rather than re-fetching, so a slow connection does not
 * leave the list stale while a request is in flight.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import { CloseIcon } from "../components/icons";
import { clock, Empty, IconButton } from "../components/ui";
import { t, useUiLang } from "../i18n";
import { addBookmark, deleteBookmark, listBookmarks, updateBookmark, type Bookmark } from "../lib/bookmarks";
import { useScrollLock } from "../lib/useScrollLock";

type IconProps = { className?: string };

function icon(path: ReactNode) {
  return function LocalIcon({ className = "h-4 w-4" }: IconProps) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
}

// Local to this file rather than components/icons.tsx: this lot owns no file
// but the two listed for it.
const PlusIcon = icon(
  <>
    <path d="M10 4v12" />
    <path d="M4 10h12" />
  </>,
);
const PencilIcon = icon(<path d="M12.5 3.5l4 4-9 9H3.5v-4z" />);
const TrashIcon = icon(
  <>
    <path d="M4 6h12" />
    <path d="M7 6V4.5A1.5 1.5 0 0 1 8.5 3h3A1.5 1.5 0 0 1 13 4.5V6" />
    <path d="M5.5 6l.6 9.5A1.5 1.5 0 0 0 7.6 17h4.8a1.5 1.5 0 0 0 1.5-1.5L14.5 6" />
  </>,
);
const CheckIcon = icon(<path d="M4 10.5l4 4 8-9" />);

export function BookmarkPanel({
  open,
  onClose,
  bookId,
  currentTime,
  onSeek,
}: {
  open: boolean;
  onClose: () => void;
  bookId: string;
  currentTime: number;
  onSeek: (time: number) => void;
}) {
  useUiLang();

  const [items, setItems] = useState<Bookmark[] | null>(null);
  const [editing, setEditing] = useState<{ id: number; note: string } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const panel = useRef<HTMLElement>(null);

  // The reading surface behind must not scroll under the finger while the
  // panel is over it, same reasoning as ChapterNav.
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    listBookmarks(bookId, controller.signal)
      .then((list) => setItems([...list].sort((a, b) => a.time - b.time)))
      .catch(() => setItems((prev) => prev ?? []));
    return () => controller.abort();
  }, [open, bookId]);

  // Closing must not leave a note field or an armed delete button behind for
  // the next time the panel opens, possibly on a different bookmark entirely.
  useEffect(() => {
    if (open) return;
    setEditing(null);
    setConfirmingDelete(null);
  }, [open]);

  // An armed delete disarms itself: a second tap elsewhere on the app should
  // not come back later and delete something the reader forgot about.
  useEffect(() => {
    if (confirmingDelete === null) return;
    const timer = window.setTimeout(() => setConfirmingDelete(null), 3000);
    return () => window.clearTimeout(timer);
  }, [confirmingDelete]);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // Deferred: the click that opened the panel must not immediately close it.
    const timer = window.setTimeout(() => document.addEventListener("mousedown", away), 0);
    document.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, onClose]);

  async function handleAdd() {
    setAdding(true);
    try {
      const created = await addBookmark(bookId, currentTime, "");
      setItems((prev) => [...(prev ?? []), created].sort((a, b) => a.time - b.time));
      // The note is optional and comes after: open its field right away
      // instead of asking the reader to find the edit button a second time.
      setEditing({ id: created.id, note: "" });
    } finally {
      setAdding(false);
    }
  }

  async function handleSaveNote() {
    if (!editing) return;
    const { id, note } = editing;
    const updated = await updateBookmark(bookId, id, note);
    setItems((prev) => (prev ?? []).map((entry) => (entry.id === id ? updated : entry)));
    setEditing(null);
  }

  async function handleDelete(id: number) {
    if (confirmingDelete !== id) {
      setConfirmingDelete(id);
      return;
    }
    await deleteBookmark(bookId, id);
    setItems((prev) => (prev ?? []).filter((entry) => entry.id !== id));
    // The deleted row's own edit field must close with it, or it goes on
    // calling updateBookmark for a bookmark that no longer exists, silently.
    setEditing((current) => (current?.id === id ? null : current));
    setConfirmingDelete(null);
  }

  function handleGo(time: number) {
    onSeek(time);
    onClose();
  }

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={
          "absolute inset-0 z-20 touch-none overscroll-contain bg-black/50 transition-opacity duration-200 " +
          (open ? "opacity-100" : "pointer-events-none opacity-0")
        }
      />
      <aside
        ref={panel}
        aria-label={t("bookmarkTitle")}
        aria-hidden={!open}
        className={
          "absolute inset-y-0 right-0 z-20 flex w-[calc(100%-2.5rem)] max-w-fib377 flex-col " +
          "border-l border-[var(--color-line)] bg-[var(--color-surface)] shadow-2xl shadow-black/60 " +
          "transition-[transform,visibility] duration-300 [transition-timing-function:var(--ease-out)] " +
          (open ? "visible translate-x-0" : "invisible translate-x-full")
        }
      >
        <div className="flex items-center justify-between gap-2 px-4 pt-4">
          <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("bookmarkTitle")}</h2>
          <IconButton label={t("bookmarkClose")} onClick={onClose} tabIndex={open ? 0 : -1}>
            <CloseIcon className="h-4 w-4" />
          </IconButton>
        </div>

        <div className="px-4 pb-[var(--spacing-fib13)] pt-[var(--spacing-fib13)]">
          <button
            type="button"
            tabIndex={open ? 0 : -1}
            onClick={handleAdd}
            disabled={adding}
            className={
              "tap flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-line)] " +
              "text-sm text-[var(--color-ink)] transition-colors duration-150 " +
              "hover:bg-[var(--color-raised)] disabled:pointer-events-none disabled:opacity-40"
            }
          >
            <PlusIcon className="h-4 w-4" />
            {t("bookmarkAddHere")}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {items && items.length === 0 && <Empty title={t("bookmarkEmpty")} />}

          {items?.map((entry) => {
            const isEditing = editing?.id === entry.id;
            return (
              <div
                key={entry.id}
                className="tap-row flex items-start gap-2 rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-[var(--color-raised)]"
              >
                {isEditing ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleSaveNote();
                    }}
                    className="flex min-w-0 flex-1 flex-col gap-[var(--spacing-fib5)]"
                  >
                    <span className="font-mono text-xs tabular-nums text-[var(--color-accent)]">
                      {clock(entry.time)}
                    </span>
                    <input
                      autoFocus
                      value={editing.note}
                      onChange={(event) => setEditing({ id: entry.id, note: event.target.value })}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          // The panel's own Escape handler would otherwise
                          // close the whole thing instead of just this field.
                          event.stopPropagation();
                          setEditing(null);
                        }
                      }}
                      placeholder={t("bookmarkNotePlaceholder")}
                      className={
                        "w-full rounded-md border border-[var(--color-line)] bg-[var(--color-page)] px-2 py-1 " +
                        "text-sm text-[var(--color-ink)] placeholder:text-[var(--color-faint)] coarse:min-h-11 " +
                        "focus:border-[var(--color-accent)] focus:outline-none"
                      }
                    />
                    <div className="flex gap-[var(--spacing-fib3)]">
                      <IconButton type="submit" label={t("bookmarkSave")}>
                        <CheckIcon className="h-4 w-4" />
                      </IconButton>
                      <IconButton type="button" label={t("bookmarkCancel")} onClick={() => setEditing(null)}>
                        <CloseIcon className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    tabIndex={open ? 0 : -1}
                    onClick={() => handleGo(entry.time)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block font-mono text-xs tabular-nums text-[var(--color-accent)]">
                      {clock(entry.time)}
                    </span>
                    {entry.note && (
                      <span className="mt-1 block truncate text-sm text-[var(--color-ink)]">{entry.note}</span>
                    )}
                  </button>
                )}

                {!isEditing && (
                  <div className="flex shrink-0 items-center gap-[var(--spacing-fib2)]">
                    <IconButton
                      label={t("bookmarkEdit")}
                      tabIndex={open ? 0 : -1}
                      onClick={() => {
                        setConfirmingDelete(null);
                        setEditing({ id: entry.id, note: entry.note });
                      }}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label={confirmingDelete === entry.id ? t("bookmarkDeleteConfirm") : t("bookmarkDelete")}
                      tabIndex={open ? 0 : -1}
                      onClick={() => void handleDelete(entry.id)}
                      className={confirmingDelete === entry.id ? "text-[var(--color-accent)]" : undefined}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </IconButton>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}
